/**
 * Audio Description Player
 * Props are updated based on changes from the MediaPlayer and Transcript
 */

import { h } from "preact";
import { useCallback, useEffect, useRef } from "preact/hooks";
import { useSignalEffect } from "@preact/signals";
import { useRecordedURL } from "../../helpers/routes";
import { playerInterface, subscribeToTimer } from "../../timer/timer-manager";

// The threshold in seconds that should trigger a correction in the
// AD playback position.
const DRIFT_THRESHOLD = 1;

type ADPlayerTypes = {
  muted: boolean;
};

const ADPlayer = ({ muted }: ADPlayerTypes) => {
  const player = useRef<HTMLMediaElement>(null); // local ref to interact with the internal player
  const url = useRecordedURL("/audio_description");

  const play = useCallback(async (): Promise<void> => {
    await player.current?.play();
  }, [player]);

  const pause = useCallback((): void => {
    player.current?.pause();
  }, [player]);

  const seek = useCallback(
    (seekTo: number): void => {
      if (player.current) {
        player.current.currentTime = seekTo;
      }
    },
    [player]
  );

  const rate = useCallback(
    (playbackRate: number): void => {
      if (player.current) {
        player.current.playbackRate = playbackRate;
      }
    },
    [player]
  );

  useSignalEffect(() => {
    const cleanupPlayHandler = playerInterface.value.onPlay(() => {
      // Sync to the correct time before starting to play again.
      seek(playerInterface.value.getCurrentTime());
      play();
    });
    const cleanupPauseHandler = playerInterface.value.onPause(() => {
      pause();
    });
    const cleanupSeekHandler = playerInterface.value.onSeek(() => {
      seek(playerInterface.value.getCurrentTime());
    });
    const cleanupPlaybackRateChangeHandler =
      playerInterface.value.onPlaybackRateChange((newRate) => {
        rate(newRate);
      });
    const cleanupBufferHandler = playerInterface.value.onBuffer(() => {
      pause();
    });

    return () => {
      cleanupPlayHandler();
      cleanupPauseHandler();
      cleanupSeekHandler();
      cleanupPlaybackRateChangeHandler();
      cleanupBufferHandler();
    };
  });

  // Correct for drift in playback time.
  useEffect(() => {
    return subscribeToTimer((currentTime) => {
      if (!player.current) return;
      if (
        Math.abs(currentTime - player.current.currentTime) > DRIFT_THRESHOLD
      ) {
        seek(currentTime);
      }
    });
  }, []);

  return (
    <audio
      ref={player}
      src={url.toString()}
      muted={muted}
      data-testid="ad-player"
    />
  );
};

export default ADPlayer;
